import { Invitation, Registerer, SessionState, UserAgent, type UserAgentOptions } from 'sip.js';

import { pruefeMikrofon } from './microphone';

/**
 * Das Telefon im Browser.
 *
 * Der Browser ist ausschließlich Bedien- und Audio-Endpunkt
 * (13_architecture.md). Er meldet sich mit plattformeigenen Einmaldaten an der
 * eigenen Telefonieebene an - die Zugangsdaten des VoIP-Anbieters bekommt er
 * nie zu sehen.
 *
 * Gesprächsaufbau und Gesprächszustand liegen serverseitig. Der Browser nimmt
 * lediglich das eingehende Audio an, das der Server ihm schickt.
 *
 * Für SIP wird SIP.js verwendet - eine eigene SIP-Implementierung im Browser
 * wäre ein unnötiges Risiko.
 */

export interface PhoneCredentials {
  username: string;
  password: string;
  websocketUrl: string;
  domain: string;
}

export type PhoneState = 'offline' | 'connecting' | 'ready' | 'on-call' | 'error';

export interface PhoneEvents {
  onState: (state: PhoneState, detail?: string) => void;
}

export class SipPhone {
  private agent?: UserAgent;
  private registerer?: Registerer;
  private current?: Invitation;
  private readonly audio: HTMLAudioElement;

  constructor(private readonly events: PhoneEvents) {
    this.audio = document.createElement('audio');
    this.audio.autoplay = true;
  }

  /**
   * Meldet das Telefon an.
   *
   * Vorher wird die Mikrofonfreigabe erfragt - ohne sie kann der Caller nicht
   * telefonieren, und das soll er verständlich erfahren.
   */
  async connect(credentials: PhoneCredentials): Promise<void> {
    this.events.onState('connecting');

    const mikrofon = await pruefeMikrofon();
    if (mikrofon !== 'ok') {
      this.events.onState('error', mikrofon);
      return;
    }

    const uri = UserAgent.makeURI('sip:' + credentials.username + '@' + credentials.domain);
    if (!uri) {
      this.events.onState('error', 'configuration');
      return;
    }

    const options: UserAgentOptions = {
      uri,
      authorizationUsername: credentials.username,
      authorizationPassword: credentials.password,
      transportOptions: { server: credentials.websocketUrl },
      sessionDescriptionHandlerFactoryOptions: {
        // Nur Sprache, kein Video.
        constraints: { audio: true, video: false },
      },
      delegate: {
        // Der Server ruft den Browser an, sobald ein Gespräch aufgebaut wird.
        onInvite: (invitation) => void this.accept(invitation),
      },
    };

    try {
      this.agent = new UserAgent(options);
      await this.agent.start();
      this.registerer = new Registerer(this.agent);
      await this.registerer.register();
      this.events.onState('ready');
    } catch {
      this.events.onState('error', 'connection');
    }
  }

  /** Nimmt den vom Server aufgebauten Kanal an und verbindet das Audio. */
  private async accept(invitation: Invitation): Promise<void> {
    this.current = invitation;

    invitation.stateChange.addListener((state: SessionState) => {
      if (state === SessionState.Established) {
        this.attachAudio(invitation);
        this.events.onState('on-call');
      }
      if (state === SessionState.Terminated) {
        this.current = undefined;
        this.audio.srcObject = null;
        this.events.onState('ready');
      }
    });

    await invitation.accept();
  }

  /** Verbindet den eingehenden Audiostrom mit dem Lautsprecher. */
  private attachAudio(invitation: Invitation): void {
    const handler = invitation.sessionDescriptionHandler as unknown as {
      peerConnection?: RTCPeerConnection;
    };
    const connection = handler.peerConnection;
    if (!connection) {
      return;
    }

    const stream = new MediaStream();
    for (const receiver of connection.getReceivers()) {
      if (receiver.track) {
        stream.addTrack(receiver.track);
      }
    }
    this.audio.srcObject = stream;
    this.audio.muted = false;
    this.audio.volume = 1;

    // `autoplay` allein reicht nicht verlässlich: das Gespräch nimmt der
    // Browser ohne Klick an, und ohne vorherige Nutzergeste darf Chrome die
    // Wiedergabe verweigern. Dann bliebe der Lautsprecher stumm, ohne jede
    // Meldung. Also ausdrücklich starten - und falls der Browser ablehnt,
    // beim nächsten Klick auf der Seite erneut.
    void this.audio.play().catch(() => {
      document.addEventListener('pointerdown', () => void this.audio.play().catch(() => undefined), {
        once: true,
      });
    });
  }

  /**
   * Stummschalten im Browser.
   *
   * Zusätzlich schaltet der Server den Kanal stumm - der Browser allein ist
   * nicht die maßgebliche Stelle.
   */
  setMuted(muted: boolean): void {
    const handler = this.current?.sessionDescriptionHandler as unknown as {
      peerConnection?: RTCPeerConnection;
    };
    for (const sender of handler?.peerConnection?.getSenders() ?? []) {
      if (sender.track) {
        sender.track.enabled = !muted;
      }
    }
  }

  async disconnect(): Promise<void> {
    try {
      await this.registerer?.unregister();
      await this.agent?.stop();
    } catch {
      // Beim Abmelden ist ein Fehler folgenlos.
    }
    this.agent = undefined;
    this.registerer = undefined;
    this.events.onState('offline');
  }
}
